import * as React from 'react'
import { Check } from 'lucide-react'
import { useUpdateOwnVendor } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { CoverPhotoField } from '@/components/CoverPhotoField'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Textarea } from '@/components/ui/textarea'
import { updateVendorInputSchema } from '@lib/input/vendor.input'
import { VENDOR_DESCRIPTION_MAX } from '@lib/constants/vendor-profile'
import type { OwnVendorProfileDto } from '@lib/dto/vendor-profile.dto'

/** The counter appears from here, so it is on screen before the limit matters. */
const COUNT_FROM = VENDOR_DESCRIPTION_MAX - 100

/**
 * The profile's logo and description: the first things venue staff and
 * planners read about a vendor. The logo saves as soon as it is chosen, like
 * every photo here; the description with its button.
 */
export function AboutCard({ profile }: { profile: OwnVendorProfileDto }) {
  const update = useUpdateOwnVendor()
  const [description, setDescription] = React.useState(profile.description ?? '')
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const count = description.trim().length

  async function save(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setSaved(false)
    const parsed = updateVendorInputSchema.safeParse({ description })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the description.')
      return
    }
    try {
      await update.mutateAsync(parsed.data)
      setSaved(true)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>About</CardTitle>
        <CardDescription>Your logo, and what you do in your own words.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div id="logo" className="space-y-2">
          <p className="text-sm font-medium">Logo</p>
          <CoverPhotoField
            kind="logo"
            coverImageUrl={profile.logoUrl}
            onSave={async (logoUrl) => {
              await update.mutateAsync({ logoUrl })
            }}
          />
        </div>
        <form id="description" className="space-y-3" onSubmit={(e) => void save(e)} noValidate>
          <Field
            label="What you do"
            htmlFor="vendor-description"
            hint={
              count >= COUNT_FROM ? (
                <span className="tabular-nums">
                  {String(count)}/{String(VENDOR_DESCRIPTION_MAX)}
                </span>
              ) : (
                'What you offer, how you work, and what sets you apart.'
              )
            }
            error={error}
          >
            <Textarea
              id="vendor-description"
              rows={6}
              maxLength={VENDOR_DESCRIPTION_MAX}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value)
                setSaved(false)
              }}
            />
          </Field>
          <FormActions>
            {saved ? (
              <span className="inline-flex items-center gap-1 text-sm text-ok-ink">
                <Check className="h-4 w-4" aria-hidden /> Saved.
              </span>
            ) : null}
            <Button type="submit" disabled={update.isPending}>
              Save
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  )
}
